import React, {
    useContext,
    useEffect,
    useState,
} from 'react';
import config from 'src/config';
import jwt_decode from "jwt-decode";
import { useDispatch, useSelector } from 'react-redux';
import { routerSelector } from 'src/selectors';
import { LOGIN, LOGOUT } from 'src/constants/routing';
import useAxios from 'axios-hooks';
import { setUser } from 'src/ducks/auth/user.duck';
import { AuthConfig } from 'src/config/interface';
import { localStorageKeys } from 'src/app.settings';
//import { useHistory } from "react-router-dom";

//import {FusionAuthClient} from 'FusionAuthClient'

declare global {
    interface Window {
        Touchless: {
            sessionId?: string;
            token?: string;
        }
    }
}
window.Touchless = { sessionId: null, token: null };

interface AuthProps {
    children: React.ReactElement;
    onRedirectCallback: (_) => void;
    initOptions: any;
    customer: string;
    config?: any;
    initialize?: any;
}

const DEFAULT_REDIRECT_CALLBACK = (_) => window.history.replaceState({}, document.title, window.location.pathname);

export const AuthContext = React.createContext(null);

export const useAuth = () => useContext(AuthContext);

export const useUpdateMetadata = (
    apiUrl: string,
    accessToken: string,
    onMetadataChange: (m: any) => void,
    user: any,
    userMetadata: any,
) => (updatedMetadata: any) => {
    const newMetadata = {
        ...userMetadata,
        ...updatedMetadata,
    };
    
    const body = {
        user: {
            email: user.email,
            data: newMetadata
        }
    };

    // fetch(`${apiUrl}user/${user.sub}`, {
    fetch(`https://api-ui.messagingkit.com/userinfo/${user?.id}`, {
        method: 'PATCH',
        headers: {
            Authorization: `Bearer ${accessToken}`,
            'content-type': 'application/json',
            'credentials': 'include'
        },
        body: JSON.stringify(body),
    }).then(() => onMetadataChange(newMetadata));
    localStorage.setItem(localStorageKeys.userMetadata, JSON.stringify(newMetadata));
    onMetadataChange(newMetadata);
};

interface IOptions {
    jwt: string;
    config: any;
    chatIdentity: string;
    session: string;
}

export const init = (options: IOptions) => {

    let savedToken = '';
    let noticeGiven = null;
    if (options?.jwt) savedToken = options?.jwt;
    
    async function handleRedirectCallback(code, redirectUrl) {
        return fetch(`https://api-ui.messagingkit.com?code=${code}&redirect_uri=${redirectUrl}`, {
            method: "GET"
        })
        .then(res => res.json())
        .then(async result => {
            savedToken = result?.access_token;
            const decoded = await decodeToken({ token: result?.access_token });
            localStorage.setItem("auth", JSON.stringify({token: result?.access_token, decoded}));
            return result
        });
    }

    async function isAuthenticated(authConfig: AuthConfig) {
        // For now, force true, as cloudflare will block access if not true
        // return true;
        const tokenFromCookies = await getTokenFromLocalstorage()
        if(tokenFromCookies) {
            // savedToken = JSON.parse(tokenFromCookies)?.token;
            const tokenExpiryTime = tokenFromCookies?.decoded["exp"];

            if(tokenExpiryTime) {
                const timeLeft = tokenExpiryTime - Math.round(Date.now() / 1000);
                if((timeLeft + 1000) < 0) {
                    // return false
                    await loginWithRedirect({ config: authConfig, fragment: authConfig?.login_redirect_uri || window.location.origin })
                }
                return true
            }
            return false
        } 
        return false
    }

    async function logout() {
        localStorage.removeItem('auth');
        window.location.href = "/logout";

        return true;
    }

    async function getUser() {
        try {
            const tokenData = await getTokenFromLocalstorage();
            if(tokenData) {
                return fetch(`https://api-ui.messagingkit.com/userinfo/${tokenData?.decoded['sub']}`, {
                    method: "GET",
                    headers: {
                        'Authorization': `Bearer ${savedToken}`,
                    }
                })
                .then(res => res.json())
                .then(result => result);
            }
        } catch (error) {
            console.log("Could not get user", error);
        }
    }
    
    async function loginWithRedirect({ config, fragment }) {
        if (config) {
            if (window.location.href.includes('textkit.statflo.com')) {
                window.location.replace(`https://sso.textkit.statflo.com/oauth2/authorize?client_id=${config?.client_id}&response_type=code&redirect_uri=${fragment}`)
            } else {
                window.location.replace(`https://ssoth.is/oauth2/authorize?client_id=${config?.client_id}&response_type=code&redirect_uri=${fragment}`)
            }
        }
    }

    async function decodeToken({ token }) {
        if(token){
            const decodedToken = jwt_decode(token) as Object
            return decodedToken
        }
        return false
    }

    async function getTokenFromLocalstorage() {
        const tokenData = localStorage.getItem('auth');
        if(tokenData) {
            return JSON.parse(tokenData);
        }
        else {
            logout()
        }
    }
    
    async function getTokenSilently(): Promise<String> {
        // if (props?.token) props.token = savedToken;
        const tokenData = await getTokenFromLocalstorage();
        const decodedToken = await decodeToken({ token: tokenData.token });
        var returnToken = tokenData?.token;
        var timeLeft = (decodedToken["exp"] - Math.round(Date.now() / 1000));
        // Redirect for now if expired
        if ((timeLeft + 1000) < 0) {

            if (noticeGiven === null) {
                alert("Your session has expired. Feel free to log back in to continue using the app.");
                noticeGiven = 'yes';

                if (typeof window !== 'undefined') {
                    if (window.location.origin.toString().indexOf("localhost") === -1) {
                        window.location.href = "/logout";
                    } else {  
                        window.location.href = "/login";
                    }
                } 

                returnToken = "Expired";
                return returnToken;
            }


        }
        return returnToken;
    }

    return { handleRedirectCallback, loginWithRedirect, isAuthenticated, getUser, logout, getTokenSilently, decodeToken };
}

export const AuthProvider = ({
    children,
    customer,
    initOptions,
    onRedirectCallback = DEFAULT_REDIRECT_CALLBACK,
    initialize
}: AuthProps) => {
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>();
    const [user, setUserData] = useState(null);
    const [token, setToken] = useState<string>(null);
    const [decodedTokenData, setDecodedTokenData] = useState(null);
    const [authClient, setAuth] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isReady, setIsReady] = useState(false);
    const [sessionId, setSessionId] = useState<string>(null);
    const [localConfig, setConfig] = useState<AuthConfig>(null);
    

    const dispatch = useDispatch();

    const [{ data: createSessionData }, sessionLogger] = useAxios({
        url: config.api.touchless,
        method: 'POST'
    }, { manual: true });

    const router = useSelector(routerSelector)

    useEffect(() => {
        const initAuth = async () => 
        (await initOptions)
            .ifNone(() => window.location.replace(config.workspaceSelectorUrl().toString()))
            .ifSome(async authConfig => {
                const authFunctions = await init(authConfig);
                setConfig(authConfig);
                setAuth(authFunctions);
                
                if (window.location.search.includes('code=') && window.location.search.includes('userState=')) {
                    const searchParam = new URLSearchParams(window.location.search)
                    if (searchParam.has('code')) {
                        const tokenData = await authFunctions.handleRedirectCallback(searchParam.get('code'), window.location.origin);
                        if(tokenData?.error) {
                            window.location.replace(`${window.location.origin}/login`);
                        } else {
                            setToken(tokenData?.access_token);
                            const decoded = await authFunctions.decodeToken({token: tokenData?.access_token});
                            dispatch(setUser(decoded?.['https://textkit.io']));
                            setDecodedTokenData(decoded);
                            const userData = await authFunctions.getUser();
                            setUserData(userData?.user);
                            setIsReady(true)
                        }
                    } else {
                        window.location.replace(`${window.location.origin}/login`);
                    }
                    // onRedirectCallback(appState);
                }

                const isAuthenticatedData = await authFunctions.isAuthenticated(authConfig);
                setIsAuthenticated(isAuthenticatedData);
                setIsReady(true);
                setLoading(false);
            })
        // };
        initAuth();
    }, []);

    useEffect(() => {
        if (createSessionData && createSessionData.data && createSessionData.data.CreateSession) {
            const sessionID = createSessionData.data.CreateSession.id;
            setSessionId(sessionID);
            window.Touchless.sessionId = sessionID;
        }
    }, [createSessionData]);

    useEffect(() => {
        const isLoginURL = router.location.pathname.startsWith(`/${LOGIN}`)
        if(!isAuthenticated && authClient && isLoginURL){
            (async() => {
                await authClient.loginWithRedirect({ config: localConfig, fragment: authClient?.redirect_uri || window.location.origin });
            })()
        }
    }, [router.location.pathname, authClient, localConfig]);

    useEffect(() => {
        const initAuthentication = async () => {
            setLoading(true);

            try {
                const token = await authClient.getTokenSilently();
                const decoded = await authClient.decodeToken({ token });
                dispatch(setUser(decoded?.['https://textkit.io']));
                setDecodedTokenData(decoded);
                setToken(token);
                setIsAuthenticated(true);
            } catch (error) {
                if (error.error !== 'login_required') {
                    throw error;
                }
            }
            setLoading(false);
        };

        if (!isAuthenticated && !loading) {
            const isLogoutURL = router.location.pathname.startsWith(`/${LOGOUT}`)
            if (isLogoutURL) {
                (async() => {
                    await authClient.loginWithRedirect({ config: localConfig, fragment: authClient?.redirect_uri || window.location.origin });
                })()
            } else {
                initAuthentication();
            }
        } else if (isAuthenticated && !loading && user) {
            setIsReady(true);
        }
    }, [isAuthenticated, loading, authClient, user, localConfig]);

    useEffect(() => {
        const initUser = async () => {
            const userData = await authClient?.getUser();
            const token = await authClient?.getTokenSilently();
            setToken(token);
            const decoded = await authClient.decodeToken({ token });
            dispatch(setUser(decoded?.['https://textkit.io']));
            setDecodedTokenData(decoded);
            setUserData(userData?.user);

            window.Touchless.token = token;
            sessionLogger({
                data: {
                query: `
                    mutation createSession {
                        CreateSession(object: {
                            started_at: "${new Date().toISOString()}",
                            identifier: "${userData?.user?.email}", 
                            hostname: "${window.location.hostname}", 
                        }) 
                        {
                            id
                        }
                    }
                `,
                },
                headers: {
                    Authorization: `Bearer ${token}`,
                    TOKEN: token,
                },
            });
        };
        if (isAuthenticated) {
            initUser();
        }
    }, [isAuthenticated]);

    const handleRedirectCallback = async () => {
        setLoading(true);
        const user = await authClient.getUser();
        setLoading(false);
        setIsAuthenticated(true);
        setUserData(user);
    };

    return (
        <AuthContext.Provider
            value={{
                isAuthenticated,
                user,
                token,
                decodedTokenData,
                isReady,
                sessionId,
                handleRedirectCallback,
                loading,
                customer,
                getTokenSilently: (...p) => authClient.getTokenSilently(...p),
                loginWithRedirect: (...p) => authClient.loginWithRedirect(...p),
                logout: (...p) => authClient.logout(...p),
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};